import Link from '@/components/common/AccessLink';
import { useAuth } from '@/contexts/AuthContext';
import { useSalesContribution } from '@/hooks/useSalesContribution';
import { saleMoney, salesDateRange } from '@klothiq/domain';
import { ArrowUpRight } from 'lucide-react';

export default function SalesPerformanceSummary({
  range: appliedRange,
}: { range?: { from: string; to: string } } = {}) {
  const { can, organization } = useAuth();
  const range = appliedRange ?? salesDateRange('month', organization?.timezone);
  const dates = `from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
  const own = useSalesContribution({ ...range, scope: 'own', include_zero: true });
  const team = useSalesContribution({ ...range, scope: 'team', limit: 5 });
  if (!can('sales_contribution.view_own') && !can('sales_contribution.view_all')) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {can('sales_contribution.view_own') && (
        <section
          className="glass-card space-y-4 p-4 sm:p-5"
          aria-label="My Sales dashboard summary"
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold">My Sales</h2>
              <p className="text-xs text-muted-foreground">
                Selected period · credited seller · balances as of now
              </p>
            </div>
            <Link
              to={`/my-sales?${dates}`}
              className="inline-flex min-h-11 items-center gap-1 text-sm text-primary"
            >
              View details
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          {own.isLoading ? (
            <p role="status">Loading your sales…</p>
          ) : own.error ? (
            <div role="alert">
              <p className="text-sm">{own.error.message}</p>
              <button onClick={() => own.refetch()} className="min-h-11 underline">
                Retry
              </button>
            </div>
          ) : (
            own.data && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['Eligible orders', own.data.totals.order_count],
                    ['Invoice value', saleMoney(own.data.totals.invoice_value)],
                    ['Net collected', saleMoney(own.data.totals.net_collected)],
                    ['Due excluding returns under review', saleMoney(own.data.totals.amount_due)],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-border bg-muted/20 p-3">
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <p className="mt-1 text-lg font-semibold">{value}</p>
                    </div>
                  ))}
                </div>
                {Number(own.data.totals.return_review_count) > 0 && (
                  <p className="text-xs text-amber-600">
                    {own.data.totals.return_review_count} returned orders need a balance review.
                  </p>
                )}
              </>
            )
          )}
        </section>
      )}
      {can('sales_contribution.view_all') && (
        <section
          className="glass-card space-y-4 p-4 sm:p-5"
          aria-label="Team Sales dashboard summary"
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold">Team Sales</h2>
              <p className="text-xs text-muted-foreground">
                Selected period · original invoice values before return adjustments
              </p>
            </div>
            <Link
              to={`/team-sales?${dates}`}
              className="inline-flex min-h-11 items-center gap-1 text-sm text-primary"
            >
              View all
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          {team.isLoading ? (
            <p role="status">Loading team sales…</p>
          ) : team.error ? (
            <div role="alert">
              <p className="text-sm">{team.error.message}</p>
              <button onClick={() => team.refetch()} className="min-h-11 underline">
                Retry
              </button>
            </div>
          ) : !team.data?.people.length ? (
            <p className="text-sm text-muted-foreground">No seller sales in this period.</p>
          ) : (
            <>
              <div className="space-y-3" aria-label="Seller invoice value comparison">
                {team.data.people.map((person) => (
                  <Link
                    key={person.person_key}
                    to={`/team-sales?${dates}&person=${encodeURIComponent(person.person_key)}`}
                    className="block rounded-lg p-1 focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <div className="mb-1 flex justify-between gap-2 text-sm">
                      <span className="min-w-0 break-words">{person.name}</span>
                      <span className="shrink-0 font-semibold">
                        {saleMoney(person.invoice_value)}
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${Math.min(100, Number(person.share_percent ?? 0))}%` }}
                      />
                    </div>
                  </Link>
                ))}
              </div>
              {Number(team.data.total_people) > team.data.people.length && (
                <p className="text-xs text-muted-foreground">
                  Other sellers:{' '}
                  {saleMoney(
                    Number(team.data.totals.invoice_value) -
                      team.data.people.reduce((s, p) => s + Number(p.invoice_value), 0),
                  )}
                  . View all for the complete breakdown.
                </p>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
